import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  AlertCircle,
  CheckCircle2,
  CreditCard,
  Globe,
  KeyRound,
  QrCode,
  RefreshCw,
  Settings as SettingsIcon,
  ShieldCheck,
} from "lucide-react";

import PaymentGatewayService, {
  GatewayCode,
  PaymentGatewayConfigInput,
  PaymentGatewayPublicConfig,
} from "../../_services/PaymentGatewayService";
import "./payment-gateway.css";

type TabKey = "overview" | "flywire" | "dbs" | "tcp" | "razorpay";

// Gateway ID ↔ name, mirroring nodefork GATEWAY_ID_TO_CODE (payment_gateway_detail.Id,
// hand-synced with the Java Constants.PaymentGateway enum). Drives the "Default gateway"
// dropdown; the stored value is the integer defaultGatewayId (0 = auto).
const GATEWAY_OPTIONS: Array<{ id: number; label: string }> = [
  { id: 8, label: "Flywire" },
  { id: 11, label: "DBS PayNow QR" },
  { id: 10, label: "2C2P" },
  { id: 6, label: "Razorpay" },
];

interface FormState {
  enabledFlywire: boolean;
  enabledDbsQr: boolean;
  enabled2C2P: boolean;
  enabledRazorpay: boolean;
  defaultGatewayId: string;
  defaultInstrumentTypeId: string;
  excessCreditTypeId: string;

  flywirePopupUrl: string;
  flywireAmountEditable: boolean;

  // dbs_qr merchant/PayNow fields (JsonConfig.payment.dbs_qr) + secret inputs.
  dbsOrgId: string;
  dbsAccountNo: string;
  dbsProxyType: "UEN" | "MOBILE";
  dbsProxyValue: string;
  dbsMerchantName: string;
  dbsMerchantCity: string;
  dbsQrExpiryMinutes: string;
  dbsQrAmountEditable: boolean;
  dbsCallbackSecret: string;
  dbsPrivateSignKey: string;
  dbsPrivateDecryptKey: string;
  // Editable — persists to iLearnAppConfig.ilearnAppEndpoint.
  dbsFrontendReturnUrl: string;

  tcpMerchantId: string;
  tcpApiBaseUrl: string;
  tcpFrontendReturnUrl: string;
  tcpSecretKey: string;
  tcpAmountEditable: boolean;

  rzpKeyId: string;
  rzpApiBaseUrl: string;
  rzpKeySecret: string;
  rzpWebhookSecret: string;
  rzpAmountEditable: boolean;
}

const EMPTY_FORM: FormState = {
  enabledFlywire: false,
  enabledDbsQr: false,
  enabled2C2P: false,
  enabledRazorpay: false,
  defaultGatewayId: "",
  defaultInstrumentTypeId: "",
  excessCreditTypeId: "",
  flywirePopupUrl: "",
  flywireAmountEditable: true,
  dbsOrgId: "",
  dbsAccountNo: "",
  dbsProxyType: "UEN",
  dbsProxyValue: "",
  dbsMerchantName: "",
  dbsMerchantCity: "",
  dbsQrExpiryMinutes: "",
  dbsQrAmountEditable: false,
  dbsCallbackSecret: "",
  dbsPrivateSignKey: "",
  dbsPrivateDecryptKey: "",
  dbsFrontendReturnUrl: "",
  tcpMerchantId: "",
  tcpApiBaseUrl: "",
  tcpFrontendReturnUrl: "",
  tcpSecretKey: "",
  tcpAmountEditable: true,
  rzpKeyId: "",
  rzpApiBaseUrl: "",
  rzpKeySecret: "",
  rzpWebhookSecret: "",
  rzpAmountEditable: true,
};

const Toggle: React.FC<{
  id: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}> = ({ id, checked, onChange }) => (
  <label htmlFor={id} className="pg-switch">
    <input
      id={id}
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
    />
    <span className="pg-slider" />
  </label>
);

const StatusPill: React.FC<{ configured: boolean; enabled: boolean }> = ({ configured, enabled }) => {
  if (!enabled) return <span className="pg-status-pill disabled">Disabled</span>;
  if (configured) {
    return (
      <span className="pg-status-pill configured">
        <CheckCircle2 size={11} /> Configured
      </span>
    );
  }
  return (
    <span className="pg-status-pill unconfigured">
      <AlertCircle size={11} /> Setup needed
    </span>
  );
};

const PaymentGatewayConfigPage: React.FC = () => {
  const history = useHistory();
  const [activeTab, setActiveTab] = useState<TabKey>("overview");
  const [config, setConfig] = useState<PaymentGatewayPublicConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [original, setOriginal] = useState<FormState>(EMPTY_FORM);

  const hydrate = useCallback((cfg: PaymentGatewayPublicConfig) => {
    setConfig(cfg);
    const next: FormState = {
      enabledFlywire: cfg.enabledGateways.includes("flywire"),
      enabledDbsQr: cfg.enabledGateways.includes("dbs_qr"),
      enabled2C2P: cfg.enabledGateways.includes("2c2p"),
      enabledRazorpay: cfg.enabledGateways.includes("razorpay"),
      defaultGatewayId: cfg.defaultGatewayId ? String(cfg.defaultGatewayId) : "",
      defaultInstrumentTypeId: cfg.defaultInstrumentTypeId ? String(cfg.defaultInstrumentTypeId) : "",
      excessCreditTypeId: cfg.excessCreditTypeId ? String(cfg.excessCreditTypeId) : "",
      flywirePopupUrl: cfg.flywire.popupUrl ?? "",
      flywireAmountEditable: cfg.flywire.amountEditable ?? true,
      dbsOrgId: cfg.dbs_qr?.orgId ?? "",
      dbsAccountNo: cfg.dbs_qr?.accountNo ?? "",
      dbsProxyType: cfg.dbs_qr?.proxyType === "MOBILE" ? "MOBILE" : "UEN",
      dbsProxyValue: cfg.dbs_qr?.proxyValue ?? "",
      dbsMerchantName: cfg.dbs_qr?.merchantName ?? "",
      dbsMerchantCity: cfg.dbs_qr?.merchantCity ?? "",
      dbsQrExpiryMinutes: cfg.dbs_qr?.qrExpiryMinutes != null ? String(cfg.dbs_qr.qrExpiryMinutes) : "",
      dbsQrAmountEditable: cfg.dbs_qr?.amountEditable ?? false,
      dbsFrontendReturnUrl: cfg.dbs_qr?.frontendReturnUrl ?? "",
      // Secret inputs always start empty — blank means "keep existing".
      dbsCallbackSecret: "",
      dbsPrivateSignKey: "",
      dbsPrivateDecryptKey: "",
      tcpMerchantId: cfg["2c2p"].merchantId ?? "",
      tcpApiBaseUrl: cfg["2c2p"].apiBaseUrl ?? "",
      tcpFrontendReturnUrl: cfg["2c2p"].frontendReturnUrl ?? "",
      tcpSecretKey: "",
      tcpAmountEditable: cfg["2c2p"].amountEditable ?? true,
      // keyId is public, so unlike every other credential here it round-trips.
      rzpKeyId: cfg.razorpay?.keyId ?? "",
      rzpApiBaseUrl: cfg.razorpay?.apiBaseUrl ?? "",
      rzpKeySecret: "",
      rzpWebhookSecret: "",
      rzpAmountEditable: cfg.razorpay?.amountEditable ?? true,
    };
    setForm(next);
    setOriginal(next);
  }, []);

  useEffect(() => {
    setLoading(true);
    PaymentGatewayService.getConfig()
      .then((res) => {
        if (res.data?.config) hydrate(res.data.config);
      })
      .catch((err) => {
        setError(err?.response?.data?.error?.message || err?.message || "Load failed");
      })
      .finally(() => setLoading(false));
  }, [hydrate]);

  useEffect(() => {
    if (!info) return;
    const t = setTimeout(() => setInfo(null), 3500);
    return () => clearTimeout(t);
  }, [info]);

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((prev) => ({ ...prev, [k]: v }));

  const dirty = useMemo(() => JSON.stringify(form) !== JSON.stringify(original), [form, original]);

  const revert = () => setForm(original);

  const save = async () => {
    setSaving(true);
    setError(null);
    setInfo(null);

    const enabled: GatewayCode[] = [];
    if (form.enabledFlywire) enabled.push("flywire");
    if (form.enabledDbsQr) enabled.push("dbs_qr");
    if (form.enabled2C2P) enabled.push("2c2p");
    if (form.enabledRazorpay) enabled.push("razorpay");

    const payload: PaymentGatewayConfigInput = {
      enabledGateways: enabled,
      defaultGatewayId: Number(form.defaultGatewayId) || 0,
      defaultInstrumentTypeId: Number(form.defaultInstrumentTypeId) || 0,
      excessCreditTypeId: Number(form.excessCreditTypeId) || 0,
      flywire: {
        popupUrl: form.flywirePopupUrl.trim(),
        amountEditable: form.flywireAmountEditable,
      },
      dbs_qr: {
        orgId: form.dbsOrgId.trim(),
        accountNo: form.dbsAccountNo.trim(),
        proxyType: form.dbsProxyType,
        proxyValue: form.dbsProxyValue.trim(),
        merchantName: form.dbsMerchantName.trim(),
        merchantCity: form.dbsMerchantCity.trim(),
        qrExpiryMinutes: Number(form.dbsQrExpiryMinutes) || undefined,
        amountEditable: form.dbsQrAmountEditable,
        frontendReturnUrl: form.dbsFrontendReturnUrl.trim(),
      },
      "2c2p": {
        merchantId: form.tcpMerchantId.trim(),
        apiBaseUrl: form.tcpApiBaseUrl.trim(),
        frontendReturnUrl: form.tcpFrontendReturnUrl.trim(),
        amountEditable: form.tcpAmountEditable,
      },
      razorpay: {
        keyId: form.rzpKeyId.trim(),
        apiBaseUrl: form.rzpApiBaseUrl.trim(),
        amountEditable: form.rzpAmountEditable,
      },
    };
    if (form.tcpSecretKey) payload["2c2p"]!.secretKey = form.tcpSecretKey;
    // Razorpay secrets: send only when newly typed (blank = keep existing).
    if (form.rzpKeySecret) payload.razorpay!.keySecret = form.rzpKeySecret;
    if (form.rzpWebhookSecret) payload.razorpay!.webhookSecret = form.rzpWebhookSecret;
    // DBS secrets: send only when a new value was typed (blank = keep existing).
    if (form.dbsCallbackSecret) payload.dbs_qr!.callbackSecret = form.dbsCallbackSecret;
    if (form.dbsPrivateSignKey) payload.dbs_qr!.ourPrivateSignKeyPem = form.dbsPrivateSignKey;
    if (form.dbsPrivateDecryptKey) payload.dbs_qr!.ourPrivateDecryptKeyPem = form.dbsPrivateDecryptKey;

    try {
      const res = await PaymentGatewayService.saveConfig(payload);
      const cfg = res.data?.config as PaymentGatewayPublicConfig | undefined;
      if (cfg) hydrate(cfg);
      setInfo("Payment gateway settings saved.");
    } catch (err: any) {
      setError(err?.response?.data?.error?.message || err?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const secretPlaceholder = (isSet: boolean, hint: string) =>
    isSet ? "•••••••• (already saved — leave blank to keep)" : hint;

  const flywireConfigured = !!config?.flywire.configured;
  const dbsConfigured = !!config?.dbs_qr.configured;
  const tcpConfigured = !!config?.["2c2p"].configured;
  const rzpConfigured = !!config?.razorpay?.configured;

  // ── Renders ────────────────────────────────────────────────────────────
  const renderOverview = () => (
    <>
      <div className="pg-card">
        <div className="pg-card-head">
          <span className="pg-card-icon"><CreditCard size={18} /></span>
          <h3>Enabled Gateways</h3>
        </div>
        <p className="pg-card-desc">
          Toggle the payment methods students see at checkout. A gateway must be configured before
          it can be enabled — switch to its tab on the left to enter credentials.
        </p>

        <div className="pg-toggle-row">
          <div className="pg-toggle-left">
            <span className="pg-toggle-icon"><Globe size={16} /></span>
            <div>
              <div className="pg-toggle-title">Flywire</div>
              <div className="pg-toggle-sub">International student payments via the legacy Java popup</div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <StatusPill configured={flywireConfigured} enabled={form.enabledFlywire} />
            <Toggle id="enable-flywire" checked={form.enabledFlywire} onChange={(v) => set("enabledFlywire", v)} />
          </div>
        </div>

        <div className="pg-toggle-row">
          <div className="pg-toggle-left">
            <span className="pg-toggle-icon"><QrCode size={16} /></span>
            <div>
              <div className="pg-toggle-title">DBS PayNow QR</div>
              <div className="pg-toggle-sub">Dynamic SGQR / PayNow EMV for SGD payments</div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <StatusPill configured={dbsConfigured} enabled={form.enabledDbsQr} />
            <Toggle id="enable-dbs" checked={form.enabledDbsQr} onChange={(v) => set("enabledDbsQr", v)} />
          </div>
        </div>

        <div className="pg-toggle-row">
          <div className="pg-toggle-left">
            <span className="pg-toggle-icon"><CreditCard size={16} /></span>
            <div>
              <div className="pg-toggle-title">2C2P</div>
              <div className="pg-toggle-sub">Cards, eWallets &amp; APMs via hosted redirect (PGW v4.3)</div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <StatusPill configured={tcpConfigured} enabled={form.enabled2C2P} />
            <Toggle id="enable-tcp" checked={form.enabled2C2P} onChange={(v) => set("enabled2C2P", v)} />
          </div>
        </div>

        <div className="pg-toggle-row">
          <div className="pg-toggle-left">
            <span className="pg-toggle-icon"><CreditCard size={16} /></span>
            <div>
              <div className="pg-toggle-title">Razorpay</div>
              <div className="pg-toggle-sub">Cards, UPI, net banking &amp; wallets for INR payments</div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <StatusPill configured={rzpConfigured} enabled={form.enabledRazorpay} />
            <Toggle id="enable-razorpay" checked={form.enabledRazorpay} onChange={(v) => set("enabledRazorpay", v)} />
          </div>
        </div>

        <div style={{ marginTop: 20 }}>
          <label className="pg-field-label" htmlFor="default-gateway">Default gateway</label>
          <select
            id="default-gateway"
            className="pg-input"
            value={form.defaultGatewayId}
            onChange={(e) => set("defaultGatewayId", e.target.value)}
          >
            <option value="">No default gateway</option>
            {GATEWAY_OPTIONS.map((g) => (
              <option key={g.id} value={String(g.id)}>{g.label}</option>
            ))}
          </select>
        
        </div>
      </div>

      <div className="pg-card">
        <div className="pg-card-head">
          <span className="pg-card-icon"><SettingsIcon size={18} /></span>
          <h3>Defaults</h3>
        </div>
        <p className="pg-card-desc">
          IDs from the master tables. These are used when posting receipts back to the ledger.
        </p>
        <div className="pg-row pg-row-2">
          <div>
            <label className="pg-field-label">Default Instrument Type ID</label>
            <input
              className="pg-input"
              type="number"
              value={form.defaultInstrumentTypeId}
              onChange={(e) => set("defaultInstrumentTypeId", e.target.value)}
              placeholder="instrument_type_master.Id"
            />
          </div>
          <div>
            <label className="pg-field-label">Excess Credit Type ID</label>
            <input
              className="pg-input"
              type="number"
              value={form.excessCreditTypeId}
              onChange={(e) => set("excessCreditTypeId", e.target.value)}
              placeholder="credit_type_master.Id (overpayment)"
            />
          </div>
        </div>
      </div>
    </>
  );

  const renderFlywire = () => (
    <div className="pg-card">
      <div className="pg-card-head">
        <span className="pg-card-icon"><Globe size={18} /></span>
        <h3>Flywire</h3>
        <span style={{ marginLeft: "auto" }}>
          <StatusPill configured={flywireConfigured} enabled={form.enabledFlywire} />
        </span>
      </div>
      {/*<p className="pg-card-desc">
        Passthrough to the legacy Java <code>paymentGateway.do</code> popup. Flywire callbacks are
        handled by Java — there's no callback URL to configure here.
      </p>*/}
      <label className="pg-field-label">Popup URL</label>
      <input
        className="pg-input mono"
        value={form.flywirePopupUrl}
        onChange={(e) => set("flywirePopupUrl", e.target.value)}
        placeholder="https://psb.tuningfork.com/popup/paymentGateway.do"
      />

      <div className="pg-toggle-row" style={{ marginTop: 18 }}>
        <div className="pg-toggle-left">
          <div>
            <div className="pg-toggle-title">Allow student to edit amount</div>
            <div className="pg-toggle-sub">
              When off, the amount on the Make Payment page is locked to the invoice value.
            </div>
          </div>
        </div>
        <Toggle
          id="flywire-amount-editable"
          checked={form.flywireAmountEditable}
          onChange={(v) => set("flywireAmountEditable", v)}
        />
      </div>
    </div>
  );

  const renderDbs = () => (
    <div className="pg-card">
      <div className="pg-card-head">
        <span className="pg-card-icon"><QrCode size={18} /></span>
        <h3>DBS PayNow QR</h3>
        <span style={{ marginLeft: "auto" }}>
          <StatusPill configured={dbsConfigured} enabled={form.enabledDbsQr} />
        </span>
      </div>
      {/*<p className="pg-card-desc">
        ilearn's <b>Make Payment</b> page fetches the QR image from the Java backend at{" "}
        <code>{`{Config.siteUrl}rest/payment/dbsqr/image`}</code> and polls{" "}
        <code>{`{Config.siteUrl}rest/payment/dbsqr/status`}</code> for settlement.
        DBS ICN posts settlement callbacks verified against the callback secret below.
      </p>*/}

      <div className="pg-section-title">Merchant &amp; PayNow Details</div>
      <div className="pg-row pg-row-2">
        <div>
          <label className="pg-field-label">Organisation ID</label>
          <input
            className="pg-input"
            value={form.dbsOrgId}
            onChange={(e) => set("dbsOrgId", e.target.value)}
            placeholder="PSB ACADEMY"
          />
        </div>
        <div>
          <label className="pg-field-label">Account No</label>
          <input
            className="pg-input mono"
            value={form.dbsAccountNo}
            onChange={(e) => set("dbsAccountNo", e.target.value)}
            placeholder="0010200019"
          />
        </div>
      </div>

      <div className="pg-row pg-row-1-2">
        <div>
          <label className="pg-field-label">Proxy Type</label>
          <select
            className="pg-select"
            value={form.dbsProxyType}
            onChange={(e) => set("dbsProxyType", e.target.value as "UEN" | "MOBILE")}
          >
            <option value="UEN">UEN</option>
            <option value="MOBILE">Mobile</option>
          </select>
        </div>
        <div>
          <label className="pg-field-label">Proxy Value (UEN / mobile)</label>
          <input
            className="pg-input mono"
            value={form.dbsProxyValue}
            onChange={(e) => set("dbsProxyValue", e.target.value)}
            placeholder="200704825E or +6512345678"
          />
        </div>
      </div>

      <div className="pg-row pg-row-3">
        <div>
          <label className="pg-field-label">Merchant Name (≤25 chars)</label>
          <input
            className="pg-input"
            value={form.dbsMerchantName}
            onChange={(e) => set("dbsMerchantName", e.target.value)}
            maxLength={25}
            placeholder="PSB ACADEMY"
          />
        </div>
        <div>
          <label className="pg-field-label">Merchant City</label>
          <input
            className="pg-input"
            value={form.dbsMerchantCity}
            onChange={(e) => set("dbsMerchantCity", e.target.value)}
            maxLength={15}
            placeholder="Singapore"
          />
        </div>
        <div>
          <label className="pg-field-label">QR Expiry (minutes)</label>
          <input
            className="pg-input"
            type="number"
            value={form.dbsQrExpiryMinutes}
            onChange={(e) => set("dbsQrExpiryMinutes", e.target.value)}
            placeholder="10"
          />
        </div>
      </div>

      <div className="pg-section-title">Return URL</div>
      <label className="pg-field-label">Frontend Return URL</label>
      <input
        className="pg-input mono"
        value={form.dbsFrontendReturnUrl}
        onChange={(e) => set("dbsFrontendReturnUrl", e.target.value)}
        placeholder="https://psb.tuningfork.com/ilearn"
      />
      {/* <p className="pg-card-desc" style={{ marginTop: 6, marginBottom: 0 }}>
        Saved to <code>iLearnAppConfig.ilearnAppEndpoint</code> — this is the shared iLearn app
        endpoint, so updating it here changes it app-wide.
      </p> */}

      {/* Callback Secret & Signing Keys section hidden per request — the
          secrets remain in JsonConfig.payment.dbs_qr and are managed elsewhere.
          Form state + save guards stay in place but no inputs are surfaced, so
          the save never overwrites the stored *Enc values. */}

      <div className="pg-toggle-row" style={{ marginTop: 18 }}>
        <div className="pg-toggle-left">
          <div>
            <div className="pg-toggle-title">Allow student to edit amount</div>
            <div className="pg-toggle-sub">
              Off by default for DBS QR — the amount is locked to the invoice value.
            </div>
          </div>
        </div>
        <Toggle
          id="dbs-amount-editable"
          checked={form.dbsQrAmountEditable}
          onChange={(v) => set("dbsQrAmountEditable", v)}
        />
      </div>
    </div>
  );

  const renderTcp = () => {
    const activeCreds = config?.["2c2p"].activeCreds ?? null;
    const isSandbox = activeCreds === "sandboxCreds";
    return (
    <div className="pg-card">
      <div className="pg-card-head">
        <span className="pg-card-icon"><CreditCard size={18} /></span>
        <h3>2C2P</h3>
        <span style={{ marginLeft: "auto" }}>
          <StatusPill configured={tcpConfigured} enabled={form.enabled2C2P} />
        </span>
      </div>
     {/* <p className="pg-card-desc">
        Hosted-page redirect against 2C2P PGW v4.3. Register the callback URL{" "}
        <code>POST /api/v1/payment-gateway/2c2p/callback</code> in the 2C2P merchant portal — it's
        verified by JWT HS256 against the merchant secret key below.
      </p>*/}

      {/* {activeCreds && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "10px 14px",
            marginBottom: 16,
            borderRadius: 10,
            fontSize: 13,
            fontWeight: 500,
            background: isSandbox
              ? "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)"
              : "linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)",
            color: isSandbox ? "#92400e" : "#166534",
            border: `1px solid ${isSandbox ? "#fde68a" : "#bbf7d0"}`,
          }}
        >
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              padding: "2px 9px",
              borderRadius: 999,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              background: isSandbox ? "#92400e" : "#166534",
              color: "var(--text-on-brand)",
            }}
          >
            {isSandbox ? "Sandbox" : "Live"}
          </span> 
         <span style={{ flex: 1 }}>
            Editing the <b>{isSandbox ? "sandbox" : "live"}</b> credentials.{" "}
            <span style={{ opacity: 0.75 }}>
              Switch sides by updating{" "}
              <code style={{ fontSize: 11.5 }}>JsonConfig.payment.2c2p.activeCreds</code>{" "}
              in the database.
            </span>
          </span>
        </div>
      )}*/}

      <div className="pg-row pg-row-1-2">
        <div>
          <label className="pg-field-label">Merchant ID</label>
          <input
            className="pg-input mono"
            value={form.tcpMerchantId}
            onChange={(e) => set("tcpMerchantId", e.target.value)}
            placeholder="JT01"
          />
        </div>
        <div>
          <label className="pg-field-label">API Base URL</label>
          <input
            className="pg-input mono"
            value={form.tcpApiBaseUrl}
            onChange={(e) => set("tcpApiBaseUrl", e.target.value)}
            placeholder="https://sandbox-pgw.2c2p.com/payment/4.3"
          />
        </div>
      </div>

      <label className="pg-field-label">Frontend Return URL (post-payment redirect)</label>
      <input
        className="pg-input mono"
        value={form.tcpFrontendReturnUrl}
        onChange={(e) => set("tcpFrontendReturnUrl", e.target.value)}
        placeholder="https://psb.tuningfork.com/ilearn/payment-result"
      />

      <label className="pg-field-label" style={{ marginTop: 14 }}>
        <KeyRound size={11} style={{ marginRight: 4, verticalAlign: "-1px" }} />
        Merchant Secret Key (HS256)
      </label>
      <input
        className="pg-input"
        type="password"
        autoComplete="new-password"
        value={form.tcpSecretKey}
        onChange={(e) => set("tcpSecretKey", e.target.value)}
        placeholder={secretPlaceholder(
          !!config?.["2c2p"].secretKeyConfigured,
          "Enter the merchant secret key from 2C2P onboarding",
        )}
      />

      <div className="pg-toggle-row" style={{ marginTop: 18 }}>
        <div className="pg-toggle-left">
          <div>
            <div className="pg-toggle-title">Allow student to edit amount</div>
            <div className="pg-toggle-sub">
              When off, the amount on the Make Payment page is locked to the invoice value.
            </div>
          </div>
        </div>
        <Toggle
          id="tcp-amount-editable"
          checked={form.tcpAmountEditable}
          onChange={(v) => set("tcpAmountEditable", v)}
        />
      </div>
    </div>
    );
  };

  const renderRazorpay = () => (
    <div className="pg-card">
      <div className="pg-card-head">
        <span className="pg-card-icon"><CreditCard size={18} /></span>
        <h3>Razorpay</h3>
        <span style={{ marginLeft: "auto" }}>
          <StatusPill configured={rzpConfigured} enabled={form.enabledRazorpay} />
        </span>
      </div>
      <p className="pg-card-desc">
        Razorpay Checkout for INR payments (cards, UPI, net banking &amp; wallets). Students pay in
        a sheet over iLearn; Razorpay posts the result back to{" "}
        <code>POST /api/v1/payment-gateway/razorpay/callback</code>, which is verified by HMAC
        against the Key Secret below. Optionally add a webhook in the Razorpay Dashboard pointing to{" "}
        <code>POST /api/v1/payment-gateway/razorpay/webhook</code> so payments still settle if a
        student closes the tab before returning.
      </p>

      {config?.razorpay?.usingLegacyCreds && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "10px 14px",
            marginBottom: 16,
            borderRadius: 10,
            fontSize: 13,
            fontWeight: 500,
            background: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
            color: "#92400e",
            border: "1px solid #fde68a",
          }}
        >
          <span style={{ flex: 1 }}>
            Currently using the <b>legacy keys</b> from the old payment gateway table, which the
            Java app still reads. They work as-is — saving this tab copies them here and encrypts
            the secret.
          </span>
        </div>
      )}

      <div className="pg-row pg-row-1-2">
        <div>
          <label className="pg-field-label">Key ID</label>
          <input
            className="pg-input mono"
            value={form.rzpKeyId}
            onChange={(e) => set("rzpKeyId", e.target.value)}
            placeholder="rzp_test_xxxxxxxxxxxx"
          />
        </div>
        <div>
          <label className="pg-field-label">API Base URL (optional)</label>
          <input
            className="pg-input mono"
            value={form.rzpApiBaseUrl}
            onChange={(e) => set("rzpApiBaseUrl", e.target.value)}
            placeholder="https://api.razorpay.com"
          />
        </div>
      </div>

      <label className="pg-field-label" style={{ marginTop: 14 }}>
        <KeyRound size={11} style={{ marginRight: 4, verticalAlign: "-1px" }} />
        Key Secret
      </label>
      <input
        className="pg-input"
        type="password"
        autoComplete="new-password"
        value={form.rzpKeySecret}
        onChange={(e) => set("rzpKeySecret", e.target.value)}
        placeholder={secretPlaceholder(
          !!config?.razorpay?.keySecretConfigured,
          "Enter the Key Secret from the Razorpay Dashboard",
        )}
      />

      <label className="pg-field-label" style={{ marginTop: 14 }}>
        <ShieldCheck size={11} style={{ marginRight: 4, verticalAlign: "-1px" }} />
        Webhook Secret (optional)
      </label>
      <input
        className="pg-input"
        type="password"
        autoComplete="new-password"
        value={form.rzpWebhookSecret}
        onChange={(e) => set("rzpWebhookSecret", e.target.value)}
        placeholder={secretPlaceholder(
          !!config?.razorpay?.webhookSecretConfigured,
          "Only needed if you configure a Razorpay webhook",
        )}
      />

      <div className="pg-toggle-row" style={{ marginTop: 18 }}>
        <div className="pg-toggle-left">
          <div>
            <div className="pg-toggle-title">Allow student to edit amount</div>
            <div className="pg-toggle-sub">
              When off, the amount on the Make Payment page is locked to the invoice value.
            </div>
          </div>
        </div>
        <Toggle
          id="rzp-amount-editable"
          checked={form.rzpAmountEditable}
          onChange={(v) => set("rzpAmountEditable", v)}
        />
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="pg-page pg-loading-wrap">
        <div className="pg-loading">
          <div className="pg-loading-spinner" />
          Loading payment gateway settings…
        </div>
      </div>
    );
  }

  return (
    <div className="pg-page">
      <aside className="pg-side">
        <h2>Payment Gateway</h2>
        <button
          type="button"
          className={`pg-nav-btn ${activeTab === "overview" ? "active" : ""}`}
          onClick={() => setActiveTab("overview")}
        >
          <SettingsIcon size={16} className="pg-nav-icon" />
          <span className="pg-nav-label">Overview</span>
        </button>
        <button
          type="button"
          className={`pg-nav-btn ${activeTab === "flywire" ? "active" : ""}`}
          onClick={() => setActiveTab("flywire")}
        >
          <Globe size={16} className="pg-nav-icon" />
          <span className="pg-nav-label">Flywire</span>
          <span className={`pg-nav-pill ${flywireConfigured ? "configured" : "unconfigured"}`}>
            {flywireConfigured ? "OK" : "Setup"}
          </span>
        </button>
        <button
          type="button"
          className={`pg-nav-btn ${activeTab === "dbs" ? "active" : ""}`}
          onClick={() => setActiveTab("dbs")}
        >
          <QrCode size={16} className="pg-nav-icon" />
          <span className="pg-nav-label">DBS PayNow QR</span>
          <span className={`pg-nav-pill ${dbsConfigured ? "configured" : "unconfigured"}`}>
            {dbsConfigured ? "OK" : "Setup"}
          </span>
        </button>
        <button
          type="button"
          className={`pg-nav-btn ${activeTab === "tcp" ? "active" : ""}`}
          onClick={() => setActiveTab("tcp")}
        >
          <CreditCard size={16} className="pg-nav-icon" />
          <span className="pg-nav-label">2C2P</span>
          <span className={`pg-nav-pill ${tcpConfigured ? "configured" : "unconfigured"}`}>
            {tcpConfigured ? "OK" : "Setup"}
          </span>
        </button>
        <button
          type="button"
          className={`pg-nav-btn ${activeTab === "razorpay" ? "active" : ""}`}
          onClick={() => setActiveTab("razorpay")}
        >
          <CreditCard size={16} className="pg-nav-icon" />
          <span className="pg-nav-label">Razorpay</span>
          <span className={`pg-nav-pill ${rzpConfigured ? "configured" : "unconfigured"}`}>
            {rzpConfigured ? "OK" : "Setup"}
          </span>
        </button>

        <div style={{ margin: "12px 8px", height: 1, background: "var(--surface-sunken)" }} />
        <button
          type="button"
          className="pg-nav-btn"
          onClick={() => history.push("/tenant-preferences")}
          style={{ fontSize: 13, color: "var(--text-muted)" }}
          title="Back to tenant preferences hub"
        >
          <ShieldCheck size={14} className="pg-nav-icon" />
          <span className="pg-nav-label">Tenant Preferences</span>
        </button>
      </aside>

      <main className="pg-main">
        <div className="pg-header">
          <div>
            <h1>
              {activeTab === "overview" && "Payment Gateway"}
              {activeTab === "flywire" && "Flywire"}
              {activeTab === "dbs" && "DBS PayNow QR"}
              {activeTab === "tcp" && "2C2P"}
              {activeTab === "razorpay" && "Razorpay"}
            </h1>
            {/*<p className="pg-subtitle">
              {activeTab === "overview" &&
                "Enable the payment methods students can choose, and set ledger defaults. Secrets are encrypted at rest and never displayed after save."}
              {activeTab === "flywire" &&
                "Passthrough to the legacy Java payment popup. Used primarily for international remittances."}
              {activeTab === "dbs" &&
                "Corporate PayNow / SGQR for SGD payments. Manage merchant details, QR expiry, the ICN callback secret, and signing keys. Secrets are encrypted at rest and never displayed after save."}
              {activeTab === "tcp" &&
                "Hosted-page redirect via 2C2P PGW v4.3 — credit/debit cards, eWallets, alternative payment methods."}
            </p>*/}
          </div>
        </div>

        {error && (
          <div className="pg-alert pg-alert-error">
            <AlertCircle size={16} className="pg-alert-icon" />
            <div className="pg-alert-body">{error}</div>
            <button type="button" className="pg-alert-close" onClick={() => setError(null)}>&times;</button>
          </div>
        )}
        {info && (
          <div className="pg-alert pg-alert-success">
            <CheckCircle2 size={16} className="pg-alert-icon" />
            <div className="pg-alert-body">{info}</div>
            <button type="button" className="pg-alert-close" onClick={() => setInfo(null)}>&times;</button>
          </div>
        )}

        {activeTab === "overview" && renderOverview()}
        {activeTab === "flywire" && renderFlywire()}
        {activeTab === "dbs" && renderDbs()}
        {activeTab === "tcp" && renderTcp()}
        {activeTab === "razorpay" && renderRazorpay()}

        <div className="pg-toolbar">
          {dirty && (
            <span className="pg-dirty-flag">
              <AlertCircle size={14} /> Unsaved changes
            </span>
          )}
          <button
            type="button"
            className="pg-btn pg-btn-secondary"
            onClick={revert}
            disabled={!dirty || saving}
          >
            <RefreshCw size={14} /> Revert
          </button>
          <button
            type="button"
            className="pg-btn pg-btn-primary"
            onClick={save}
            disabled={!dirty || saving}
          >
            {saving ? "Saving…" : "Save Settings"}
          </button>
        </div>
      </main>
    </div>
  );
};

export default PaymentGatewayConfigPage;
